import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Optional, Output, SimpleChanges } from '@angular/core';
import { FormButton } from '@app/core/components/form-field-button/form-field-button.component';
import { LayoutService } from '@app/core/services/layout.service';
import { ModalService } from '@app/core/services/modal.service';
import { TranslateService } from '@app/core/services/translate.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { P2SubscriptionGet, P2SubscriptionService } from '@app/services/p2-subscription.service';

@Component({
    selector: 'tt-p2-subscription-edit',
    templateUrl: './p2-subscription-edit.component.html',
    styleUrls: ['./p2-subscription-edit.component.css'],
    standalone: false,
})
export class P2SubscriptionEditComponent implements OnInit, OnChanges {
    @Input() public p2SubscriptionKeyno?: string;

    @Input() public inline = false;

    @Output() public onSave = new EventEmitter<void>();

    public page?: P2SubscriptionGet;

    public saving = false;

    public readonly tabletSize: MediaQueryList;

    public readonly headingButtons: FormButton[] = [{
        id: 'goto',
        type: 'primary',
        icon: 'fas fa-chevron-right',
        goto: 'translateedit',
        gotoParms: () => this.page?.word_id_heading ?? '',
    }];

    public readonly descriptionButtons: FormButton[] = [{
        id: 'goto',
        type: 'primary',
        icon: 'fas fa-chevron-right',
        goto: 'translateedit',
        gotoParms: () => this.page?.word_id_description ?? '',
    }];

    private navigator?: TTViewNavigator;

    private translations = {
        error: 'error',
        ok: 'ok'
    };

    constructor(
        private subscriptionService: P2SubscriptionService,
        private modal: ModalService,
        private translate: TranslateService,
        layout: LayoutService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) navigator?: TTViewNavigator,
    ) {
        this.tabletSize = layout.getMediaQueries().tablet;
        this.navigator = useNavigatorFor(navigator, this);
    }

    public async saveChanges(): Promise<void> {
        if (!this.page || this.saving) return;

        this.saving = true;

        try {
            const response = await this.subscriptionService.saveSubscription(this.page);

            if (response.iserror !== '0') {
                await this.modal.show({
                    type: 'warning',
                    title: this.translations.error,
                    message: response.message,
                    buttons: [{
                        label: this.translations.ok,
                        cssClass: 'btn-warning',
                        close: true
                    }]
                });

                return;
            }

            if (this.tabletSize.matches && this.inline) {
                this.onSave.emit();
            } else {
                await this.navigator?.back();
            }
        } catch {
            // Keep the editor open and unlock saving so the user can retry.
        } finally {
            this.saving = false;
        }
    }

    public ngOnInit(): void {
        void this.loadTranslations();

        if (!this.p2SubscriptionKeyno) {
            const routeKeyno = this.navigator?.params['p2_subscription_keyno'];

            if (routeKeyno !== undefined && routeKeyno !== null) {
                this.p2SubscriptionKeyno = `${routeKeyno}`;
                void this.loadPage(this.p2SubscriptionKeyno);
            }
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const keyno = changes['p2SubscriptionKeyno']?.currentValue;

        if (keyno !== undefined && keyno !== null && keyno !== '') {
            this.p2SubscriptionKeyno = `${keyno}`;
            void this.loadPage(this.p2SubscriptionKeyno);
        }
    }

    private async loadPage(keyno: string): Promise<void> {
        this.page = await this.subscriptionService.getSubscription({ p2_subscription_keyno: keyno });
    }

    private async loadTranslations(): Promise<void> {
        try {
            const translations = await this.translate.translateBatch(['error', 'ok'] as const);
            this.translations = { error: translations.error, ok: translations.ok };
        } catch {
            // Keep the translation IDs as fallback labels if translation loading fails.
        }
    }
}
